import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useParams } from "react-router-dom";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Layout from "../../components/Layout";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import { getApiPath } from "../../config/api.config";
import { cardInterestsService } from "../../services/card-interests.service";
import {
  CardInterestHistoryChartResponse,
  CardInterestListingCard,
  CardOfInterest,
} from "../../types/card-interest";

const formatPrice = (currency: string | null, priceAmount: string | null) => {
  if (!currency || !priceAmount) return "—";
  const num = Number(priceAmount);
  if (Number.isNaN(num)) return `${currency} ${priceAmount}`;
  return `${currency} ${num.toFixed(2)}`;
};

const getListingDate = (item: CardInterestListingCard) =>
  item.postedAt || item.lastSeenAt || item.firstSeenAt || item.createdAt;

const getWhatsappSenderLink = (senderNumber: string | null): string | null => {
  if (!senderNumber) return null;
  const normalized = senderNumber.replace(/[^\d]/g, "");
  if (!normalized) return null;
  return `https://wa.me/${normalized}`;
};

const resolveListingImageSrc = (imageUrl: string | null): string | null => {
  if (!imageUrl) return null;
  if (/^(https?:)?\/\//i.test(imageUrl) || imageUrl.startsWith("data:")) {
    return imageUrl;
  }
  return getApiPath(imageUrl);
};

const formatBadgeLabel = (value: string | null) => {
  if (!value) return "—";
  return value
    .split(/[_-]+/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const CardInterestDetail = () => {
  const { t } = useTranslation("common");
  const { id } = useParams();
  const apiBaseUrl = process.env.REACT_APP_API_URL || "http://localhost:3000/api";

  const [interest, setInterest] = useState<CardOfInterest | null>(null);
  const [items, setItems] = useState<CardInterestListingCard[]>([]);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [referenceFile, setReferenceFile] = useState<File | null>(null);
  const [imageMatchRequired, setImageMatchRequired] = useState(false);
  const [similarityThreshold, setSimilarityThreshold] = useState("0.32");
  const [error, setError] = useState("");
  const [historyChart, setHistoryChart] =
    useState<CardInterestHistoryChartResponse | null>(null);
  const [sortKey, setSortKey] = useState<"datetime" | "price">("datetime");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const displayCurrency = useMemo(() => {
    const fromItems =
      items.find((i) => i.currency || i.listingPriceCurrency || i.pricePointCurrency);
    const itemCurrency =
      fromItems?.currency ||
      fromItems?.listingPriceCurrency ||
      fromItems?.pricePointCurrency ||
      null;
    return itemCurrency || historyChart?.currency || null;
  }, [items, historyChart]);

  const kpis = useMemo(() => {
    const active = items
      .filter((i) => !i.isSold)
      .map((i) =>
        i.pricePointAmount != null
          ? Number(i.pricePointAmount)
          : i.priceAmount != null
            ? Number(i.priceAmount)
            : NaN,
      )
      .filter((n) => Number.isFinite(n));

    const activeHigh = active.length ? Math.max(...active) : null;
    const activeLow = active.length ? Math.min(...active) : null;

    return {
      historicalHigh: historyChart?.high ?? null,
      historicalLow: historyChart?.low ?? null,
      activeHigh,
      activeLow,
    };
  }, [items, historyChart]);

  const title = useMemo(() => {
    if (!interest) return t("card_interests");
    return interest.name || interest.keywords || t("card_interests");
  }, [interest, t]);

  const load = useCallback(
    async (nextPage: number) => {
      if (!id) return;
      setError("");
      try {
        setIsLoading(true);

        const [interestRes, listingsRes, historyRes] = await Promise.all([
          cardInterestsService.getOne(id),
          cardInterestsService.listListings(id, {
            page: nextPage,
            take: 48,
            orderBy: sortKey,
            order: sortDirection.toUpperCase() as "ASC" | "DESC",
          }),
          cardInterestsService.getHistoryChart(id, 365),
        ]);

        setInterest(interestRes);
        setTotalItems(listingsRes.meta.itemCount);
        setPageCount(listingsRes.meta.pageCount);
        setHistoryChart(historyRes);
        setItems(listingsRes.data);
      } catch (e: any) {
        setError(e?.message || "Failed to load");
      } finally {
        setIsLoading(false);
      }
    },
    [id, sortDirection, sortKey],
  );

  useEffect(() => {
    load(page);
  }, [load, page]);

  useEffect(() => {
    if (!interest) return;
    setImageMatchRequired(interest.imageMatchRequired === true);
    setSimilarityThreshold(
      typeof interest.similarityThreshold === "number"
        ? String(interest.similarityThreshold)
        : "0.32",
    );
  }, [interest]);

  const runNow = async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      await cardInterestsService.runNow(id);
      if (page !== 1) {
        setPage(1);
      } else {
        await load(1);
      }
    } catch (e: any) {
      setError(e?.message || "Failed to run");
      setIsLoading(false);
    }
  };

  const goToPage = (nextPage: number) => {
    if (nextPage < 1 || nextPage > pageCount || nextPage === page) return;
    setPage(nextPage);
  };

  const uploadReference = async () => {
    if (!id || !referenceFile) return;
    try {
      setIsSaving(true);
      const updated = await cardInterestsService.uploadReferenceImage(
        id,
        referenceFile,
      );
      setReferenceFile(null);
      setInterest(updated);
    } catch (e: any) {
      setError(e?.message || "Failed to upload");
    } finally {
      setIsSaving(false);
    }
  };

  const saveMatchSettings = async () => {
    if (!id) return;
    const n = Number(similarityThreshold);
    try {
      setIsSaving(true);
      const updated = await cardInterestsService.update(id, {
        imageMatchRequired,
        similarityThreshold: Number.isFinite(n) ? n : 0.32,
      });
      setInterest(updated);
    } catch (e: any) {
      setError(e?.message || "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const chartStroke = "#2563eb";

  const CustomTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: any[];
    label?: string;
  }) => {
    if (!active || !payload || !payload.length) return null;
    const avg = payload.find((p) => p.dataKey === "avg")?.value;
    const valueText =
      typeof avg === "number" && Number.isFinite(avg)
        ? formatPrice(displayCurrency, String(avg))
        : "—";

    return (
      <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 shadow-sm px-3 py-2">
        <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
        <div className="mt-1 flex items-center gap-2">
          <span
            className="inline-block w-2.5 h-2.5 rounded-sm"
            style={{ backgroundColor: chartStroke }}
          />
          <div className="text-sm font-semibold tabular-nums">{valueText}</div>
        </div>
      </div>
    );
  };

  const setSort = (nextKey: "datetime" | "price") => {
    if (sortKey === nextKey) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      setPage(1);
      return;
    }
    setSortKey(nextKey);
    setSortDirection("desc");
    setPage(1);
  };

  return (
    <Layout title={title}>
      <div className="space-y-4 flex flex-col">
        <Card className="p-4 shadow-sm flex-shrink-0">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <h1 className="text-2xl font-semibold tracking-tight truncate">
                  {title}
                </h1>
                {interest?.keywords && (
                  <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                    {interest.keywords}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <NavLink to="/card-interests">
                  <Button size="sm" variant="secondary">
                    {t("back")}
                  </Button>
                </NavLink>
                <Button
                  size="sm"
                  onClick={runNow}
                  disabled={!interest?.enabled}
                  isLoading={isLoading}
                >
                  {t("run_now")}
                </Button>
              </div>
            </div>

            {error && (
              <div className="p-3 rounded-md bg-red-50 text-red-600 border border-red-100">
                {error}
              </div>
            )}

            {interest && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-28 h-28 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900 overflow-hidden flex items-center justify-center flex-shrink-0">
                    {interest.referenceImageUrl ? (
                      <img
                        src={`${apiBaseUrl}/card-interests/${interest.id}/reference-image`}
                        alt={t("reference_image")}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-xs text-gray-400">{t("no_image")}</div>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {t("reference_image")}
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        setReferenceFile(
                          e.target.files && e.target.files[0]
                            ? e.target.files[0]
                            : null,
                        )
                      }
                    />
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={uploadReference}
                      disabled={!referenceFile}
                      isLoading={isSaving}
                    >
                      {t("upload")}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t("image_matching")}
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                    <input
                      type="checkbox"
                      checked={imageMatchRequired}
                      onChange={(e) => setImageMatchRequired(e.target.checked)}
                    />
                    {t("require_image_match_facebook")}
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      {t("threshold")}
                    </div>
                    <input
                      value={similarityThreshold}
                      onChange={(e) => setSimilarityThreshold(e.target.value)}
                      className="w-24 rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={saveMatchSettings}
                      isLoading={isSaving}
                    >
                      {t("save")}
                    </Button>
                  </div>
                </div>

              </div>
            )}
          </div>
        </Card>

        <Card className="p-4 shadow-sm flex-shrink-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="p-3 rounded-md border border-gray-200 dark:border-gray-700">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {t("highest_historical_price")}
              </div>
              <div className="text-lg font-semibold tabular-nums">
                {kpis.historicalHigh != null
                  ? formatPrice(displayCurrency, String(kpis.historicalHigh))
                  : "—"}
              </div>
            </div>
            <div className="p-3 rounded-md border border-gray-200 dark:border-gray-700">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {t("lowest_historical_price")}
              </div>
              <div className="text-lg font-semibold tabular-nums">
                {kpis.historicalLow != null
                  ? formatPrice(displayCurrency, String(kpis.historicalLow))
                  : "—"}
              </div>
            </div>
            <div className="p-3 rounded-md border border-gray-200 dark:border-gray-700">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {t("highest_active_price")}
              </div>
              <div className="text-lg font-semibold tabular-nums">
                {kpis.activeHigh != null
                  ? formatPrice(displayCurrency, String(kpis.activeHigh))
                  : "—"}
              </div>
            </div>
            <div className="p-3 rounded-md border border-gray-200 dark:border-gray-700">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {t("lowest_active_price")}
              </div>
              <div className="text-lg font-semibold tabular-nums">
                {kpis.activeLow != null
                  ? formatPrice(displayCurrency, String(kpis.activeLow))
                  : "—"}
              </div>
            </div>
          </div>

          <div className="mt-4">
            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {t("historical_price_chart")}
            </div>
            <div className="mt-2 h-64 w-full">
              {historyChart && historyChart.points.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={historyChart.points}>
                    <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                      content={<CustomTooltip />}
                      cursor={{ stroke: chartStroke, strokeOpacity: 0.25 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="avg"
                      stroke={chartStroke}
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-700 rounded-md">
                  {t("noData")}
                </div>
              )}
            </div>
          </div>
        </Card>

        <div className="flex-1">
          {isLoading && items.length === 0 ? (
            <div className="p-6 text-sm text-gray-500 dark:text-gray-400">
              Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="p-6 text-sm text-gray-500 dark:text-gray-400">
              {t("noData")}
            </div>
          ) : (
            <Card className="shadow-sm overflow-hidden">
              <div className="p-4 pb-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t("listing_grids")}
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    {t("sort_by")}
                  </div>
                  <button
                    type="button"
                    onClick={() => setSort("datetime")}
                    className={[
                      "px-3 py-2 rounded-md border text-sm font-medium",
                      sortKey === "datetime"
                        ? "border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-900/20 dark:text-blue-200"
                        : "border-gray-300 bg-white text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200",
                    ].join(" ")}
                  >
                    {t("sort_datetime")}{" "}
                    {sortKey === "datetime"
                      ? sortDirection === "asc"
                        ? "↑"
                        : "↓"
                      : ""}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSort("price")}
                    className={[
                      "px-3 py-2 rounded-md border text-sm font-medium",
                      sortKey === "price"
                        ? "border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-900/20 dark:text-blue-200"
                        : "border-gray-300 bg-white text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200",
                    ].join(" ")}
                  >
                    {t("sort_price")}{" "}
                    {sortKey === "price"
                      ? sortDirection === "asc"
                        ? "↑"
                        : "↓"
                      : ""}
                  </button>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div className="px-4 text-sm text-gray-600 dark:text-gray-300">
                  {items.length} {t("of")} {totalItems} {t("results")}
                </div>
                <div className="px-4 text-sm text-gray-600 dark:text-gray-300">
                  {page} / {pageCount || 1}
                </div>
              </div>
              <div className="overflow-x-auto themed-scrollbar themed-scrollbar-prominent">
                <table className="min-w-full text-sm divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-xs text-gray-700 dark:text-gray-200">
                        {t("title")}
                      </th>
                      <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-xs text-gray-700 dark:text-gray-200">
                        {t("source")}
                      </th>
                      <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-xs text-gray-700 dark:text-gray-200">
                        {t("status")}
                      </th>
                      <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-xs text-gray-700 dark:text-gray-200">
                        {t("listing_price")}
                      </th>
                      <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-xs text-gray-700 dark:text-gray-200">
                        {t("price_point")}
                      </th>
                      <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-xs text-gray-700 dark:text-gray-200">
                        {t("condition")}
                      </th>
                      <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-xs text-gray-700 dark:text-gray-200">
                        {t("sort_datetime")}
                      </th>
                      <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-xs text-gray-700 dark:text-gray-200">
                        {t("actions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {items.map((item, index) => {
                      const grade = [item.gradeCompany, item.gradeValue]
                        .filter(Boolean)
                        .join(" ");
                      const listingDate = getListingDate(item);
                      const senderLink =
                        item.sourceChannel === "whatsapp"
                          ? getWhatsappSenderLink(item.senderNumber)
                          : null;
                      const imageSrc = resolveListingImageSrc(item.imageUrl);
                      return (
                        <tr
                          key={item.id}
                          className={
                            index % 2 === 0
                              ? "bg-white dark:bg-gray-900"
                              : "bg-gray-50 dark:bg-gray-800/60"
                          }
                        >
                          <td className="px-4 py-4 align-top min-w-[280px]">
                            <div className="flex items-start gap-3">
                              <div className="w-16 h-16 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900 overflow-hidden flex-shrink-0 flex items-center justify-center">
                                {imageSrc ? (
                                  <img
                                    src={imageSrc}
                                    alt={item.title}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                  />
                                ) : (
                                  <div className="text-[10px] text-gray-400 text-center px-1">
                                    {t("no_image")}
                                  </div>
                                )}
                              </div>
                              <div className="min-w-0 space-y-1">
                                <a
                                  href={item.sourceUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="font-medium text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 line-clamp-2"
                                >
                                  {item.title}
                                </a>
                                <a
                                  href={item.sourceUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline break-all"
                                >
                                  {t("source_link")}
                                </a>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 align-top min-w-[220px]">
                            <div className="space-y-2">
                              <div className="font-medium text-gray-900 dark:text-gray-100">
                                {item.sourceName || item.sourceId}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {item.sourceChannel && (
                                  <span className="px-2 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-200 border border-blue-200 dark:border-blue-800">
                                    {formatBadgeLabel(item.sourceChannel)}
                                  </span>
                                )}
                                {item.sourceType && (
                                  <span className="px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600">
                                    {formatBadgeLabel(item.sourceType)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 align-top">
                            <span
                              className={[
                                "inline-flex px-2 py-1 rounded-md text-xs font-semibold border",
                                item.isSold
                                  ? "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-200 dark:border-red-800"
                                  : "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-200 dark:border-green-800",
                              ].join(" ")}
                            >
                              {item.isSold ? t("sold") : t("active")}
                            </span>
                          </td>
                          <td className="px-4 py-4 align-top">
                            <div className="font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                              {formatPrice(
                                item.listingPriceCurrency || item.currency,
                                item.listingPriceAmount,
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 align-top min-w-[160px]">
                            <div className="space-y-1">
                              <div className="font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                                {formatPrice(
                                  item.pricePointCurrency || item.currency,
                                  item.pricePointAmount,
                                )}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {item.pricePointCapturedAt
                                  ? new Date(
                                      item.pricePointCapturedAt,
                                    ).toLocaleString()
                                  : "—"}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 align-top text-gray-700 dark:text-gray-200">
                            {grade || "—"}
                          </td>
                          <td className="px-4 py-4 align-top text-gray-700 dark:text-gray-200 min-w-[170px]">
                            {listingDate
                              ? new Date(listingDate).toLocaleString()
                              : "—"}
                          </td>
                          <td className="px-4 py-4 align-top min-w-[220px]">
                            {item.sourceChannel === "whatsapp" ? (
                              <div className="space-y-2">
                                <div className="space-y-1 text-xs text-gray-500 dark:text-gray-400">
                                  <div>
                                    {t("sender")}: {item.senderName || "—"}
                                  </div>
                                  <div>
                                    {t("sender_number")}: {item.senderNumber || "—"}
                                  </div>
                                </div>
                                {senderLink ? (
                                  <a
                                    href={senderLink}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex h-8 items-center justify-center whitespace-nowrap rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
                                  >
                                    {t("message_sender")}
                                  </a>
                                ) : (
                                  <span className="text-sm text-gray-400">—</span>
                                )}
                              </div>
                            ) : (
                              <a
                                href={item.sourceUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex h-8 items-center justify-center whitespace-nowrap rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
                              >
                                {t("source_link")}
                              </a>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  {items.length} {t("of")} {totalItems} {t("results")}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => goToPage(page - 1)}
                    disabled={page <= 1 || isLoading}
                  >
                    {t("prev")}
                  </Button>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {page} / {pageCount || 1}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => goToPage(page + 1)}
                    disabled={page >= pageCount || isLoading}
                  >
                    {t("next")}
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default CardInterestDetail;
